import api from './api';
import { getSessionToken } from './globalAssistantApi';

// Worker URL for direct streaming calls (bypasses Vercel proxy buffering)
// Local dev: worker runs on port 8080
// Production: Railway at service.greenfirefly.ai
const WORKER_URL = import.meta.env.VITE_WORKER_URL ||
  (import.meta.env.DEV ? 'http://localhost:8080' : 'https://service.greenfirefly.ai');

export interface RunRequest {
  agent_id: string;
  input_variables: Record<string, string>;
  user_prompt: string;
  agent_content?: string; // Optional: run with modified content without saving
}

export interface RunResponse {
  trajectory_id: string;
  status: string;
}

export interface ChatRequest {
  agent_id: string;
  trajectory_id?: string;
  messages: Array<{ role: 'user' | 'assistant'; content: string }>;
  input_variables: Record<string, string>;
  agent_content?: string;
}

export interface StopResponse {
  success: boolean;
  message?: string;
}

export interface TrajectoryStatus {
  id: string;
  status: 'running' | 'completed' | 'failed';
  error?: string;
  result?: any;
}

export const playgroundApi = {
  /**
   * Run an agent in one-shot mode
   * Uses session token for Railway worker authentication
   */
  async run(request: RunRequest): Promise<RunResponse> {
    const sessionToken = await getSessionToken();
    const response = await fetch(`${WORKER_URL}/api/playground/run`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${sessionToken}`,
      },
      credentials: 'include',
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: response.statusText }));
      throw new Error(error.detail || `HTTP error: ${response.status}`);
    }

    return response.json();
  },

  /**
   * Send a chat message to an agent (SSE stream)
   * Returns an AbortController to cancel the stream
   * Uses session token for Railway worker authentication
   */
  chat(
    request: ChatRequest,
    onMessage: (data: any) => void,
    onError: (error: Error) => void,
    onComplete: () => void
  ): AbortController {
    const controller = new AbortController();

    // Get session token then start streaming
    getSessionToken()
      .then((sessionToken) => {
        return fetch(`${WORKER_URL}/api/playground/chat`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${sessionToken}`,
          },
          credentials: 'include',
          body: JSON.stringify(request),
          signal: controller.signal,
        });
      })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(`HTTP error: ${response.status}`);
        }

        const reader = response.body?.getReader();
        if (!reader) {
          throw new Error('No response body');
        }

        const decoder = new TextDecoder();

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value, { stream: true });
          const lines = chunk.split('\n');

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              const data = line.slice(6);
              if (data === '[DONE]') {
                onComplete();
                return;
              }

              try {
                const parsed = JSON.parse(data);
                onMessage(parsed);
              } catch {
                // Skip malformed JSON
              }
            }
          }
        }

        onComplete();
      })
      .catch((error) => {
        if (error.name !== 'AbortError') {
          onError(error);
        }
      });

    return controller;
  },

  /**
   * Stop a running execution
   * Uses session token for Railway worker authentication
   */
  async stop(trajectoryId: string): Promise<StopResponse> {
    const sessionToken = await getSessionToken();
    const response = await fetch(`${WORKER_URL}/api/playground/stop/${trajectoryId}`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${sessionToken}`,
      },
      credentials: 'include',
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: response.statusText }));
      throw new Error(error.detail || `HTTP error: ${response.status}`);
    }

    return response.json();
  },

  /**
   * Get trajectory status
   */
  async getStatus(trajectoryId: string): Promise<TrajectoryStatus> {
    const response = await api.get(`/trajectories/${trajectoryId}`);
    // API returns {status: "success", data: {id, status, ...}}
    const apiData = response.data;
    return {
      id: apiData.data?.id || trajectoryId,
      status: apiData.data?.status || 'unknown',
      error: apiData.data?.error,
      result: apiData.data?.final_output,
    };
  },

  /**
   * Get trajectory logs
   */
  async getLogs(trajectoryId: string): Promise<{ log_content: string }> {
    const response = await api.get(`/trajectories/${trajectoryId}/logs`);
    return response.data;
  },

  /**
   * Get trajectory events
   */
  async getEvents(trajectoryId: string): Promise<{ events: any[] }> {
    const response = await api.get(`/trajectories/${trajectoryId}/events`);
    return response.data;
  },

  /**
   * Poll for trajectory updates (logs, events, status, result)
   * Returns a function to stop polling
   *
   * Options:
   * - interval: poll interval in ms (default 2000)
   * - stopOnComplete: stop polling when status is not 'running' (default true)
   */
  poll(
    trajectoryId: string,
    onUpdate: (data: { logs: string; events: any[]; status: string; result?: string }) => void,
    interval: number = 2000,
    stopOnComplete: boolean = true
  ): () => void {
    let active = true;

    const doPoll = async () => {
      if (!active) return;

      try {
        const [logsRes, eventsRes, statusRes] = await Promise.all([
          playgroundApi.getLogs(trajectoryId).catch(() => ({ log_content: '' })),
          playgroundApi.getEvents(trajectoryId).catch(() => ({ events: [] })),
          playgroundApi.getStatus(trajectoryId).catch(() => ({ status: 'unknown', result: undefined })),
        ]);

        if (active) {
          onUpdate({
            logs: logsRes.log_content,
            events: eventsRes.events,
            status: statusRes.status || 'unknown',
            result: statusRes.result,
          });

          // Stop polling if execution is complete (but not on 'unknown' which may be transient)
          if (stopOnComplete && statusRes.status && statusRes.status !== 'running' && statusRes.status !== 'unknown') {
            active = false;
            return;
          }
        }
      } catch (error) {
        console.error('Polling error:', error);
      }

      if (active) {
        setTimeout(doPoll, interval);
      }
    };

    doPoll();

    return () => {
      active = false;
    };
  },
};

export default playgroundApi;
